import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  fetchPublicPolicyByHandle,
  HANDLE_TO_TYPE,
  POLICY_LABELS,
  TYPE_TO_HANDLE,
  type ShopPolicy,
  type PolicyType,
} from '@/lib/shopifyPolicies';

const FOOTER_HANDLES: PolicyType[] = [
  'PRIVACY_POLICY',
  'REFUND_POLICY',
  'TERMS_OF_SERVICE',
  'SHIPPING_POLICY',
];

export default function Policy() {
  const { handle = '' } = useParams();
  const [policy, setPolicy] = useState<ShopPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const p = await fetchPublicPolicyByHandle(handle);
        if (cancelled) return;
        if (!p || !p.body) setNotFound(true);
        setPolicy(p);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [handle]);

  const fallbackTitle = HANDLE_TO_TYPE[handle] ? POLICY_LABELS[HANDLE_TO_TYPE[handle]] : 'מדיניות';

  useEffect(() => {
    document.title = `${policy?.title || fallbackTitle} | קליניקת ריהאם`;
  }, [policy, fallbackTitle]);

  return (
    <div dir="rtl" className="bg-[#EAE6D7] min-h-screen">
      <article className="container mx-auto max-w-3xl px-6 py-16 text-[#412018]">
        <nav className="text-xs uppercase tracking-widest text-[#412018]/70 mb-6">
          <Link to="/" className="hover:underline">בית</Link>
          <span className="mx-2">/</span>
          <span>{policy?.title || fallbackTitle}</span>
        </nav>

        <h1 className="text-4xl md:text-5xl font-serif mb-8">
          {policy?.title || fallbackTitle}
        </h1>

        {loading && <p className="text-[#412018]/70">טוען…</p>}

        {!loading && notFound && (
          <div className="space-y-6">
            <p className="text-[#412018]/80">
              עדיין לא נוצר תוכן עבור מדיניות זו. אנא בדקו שוב בקרוב.
            </p>
            <div className="text-sm">
              <p className="mb-2 font-semibold">מדיניות נוספת:</p>
              <ul className="list-disc ms-6 space-y-1">
                {FOOTER_HANDLES.map((t) => (
                  <li key={t}>
                    <Link to={`/policies/${TYPE_TO_HANDLE[t]}`} className="underline hover:no-underline">
                      {POLICY_LABELS[t]}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {!loading && policy?.body && (
          <div
            className="policy-content prose prose-neutral max-w-none text-[#412018]/90 leading-relaxed"
            // Body comes from Shopify shop policies (admin-controlled). Treated as trusted HTML.
            dangerouslySetInnerHTML={{ __html: policy.body }}
          />
        )}
      </article>
    </div>
  );
}
